import type { JsonSchema, JsonValue } from '../api/types';
import { isPlainObject } from './json';

export interface SchemaAnnotations {
  literal: boolean;
  secretAllowed: boolean;
  secretRequired: boolean;
}

export const NO_ANNOTATIONS: SchemaAnnotations = {
  literal: false,
  secretAllowed: false,
  secretRequired: false,
};

export function ownAnnotations(schema: unknown): SchemaAnnotations {
  if (!isPlainObject(schema)) return NO_ANNOTATIONS;
  return {
    literal: schema['x-nebisys-literal'] === true,
    secretAllowed: schema['x-nebisys-secret-allowed'] === true,
    secretRequired: schema['x-nebisys-secret-required'] === true,
  };
}

function merge(a: SchemaAnnotations, b: SchemaAnnotations): SchemaAnnotations {
  return {
    literal: a.literal || b.literal,
    secretAllowed: a.secretAllowed || b.secretAllowed,
    secretRequired: a.secretRequired || b.secretRequired,
  };
}

/** The subschema and its `allOf` members: all evaluate the same instance location. */
function evaluated(schema: unknown): Record<string, unknown>[] {
  if (!isPlainObject(schema)) return [];
  const result = [schema];
  if (Array.isArray(schema.allOf)) for (const member of schema.allOf) result.push(...evaluated(member));
  return result;
}

function childSchemas(schema: Record<string, unknown>, segment: string | number): unknown[] {
  const result: unknown[] = [];
  const key = String(segment);
  const properties = schema.properties;
  if (isPlainObject(properties) && key in properties) {
    result.push(properties[key]);
  } else if (isPlainObject(schema.additionalProperties)) {
    result.push(schema.additionalProperties);
  }
  if (/^\d+$/.test(key)) {
    const index = Number(key);
    if (Array.isArray(schema.prefixItems) && index < schema.prefixItems.length) {
      result.push(schema.prefixItems[index]);
    } else if (isPlainObject(schema.items)) {
      result.push(schema.items);
    }
  }
  return result;
}

/**
 * The x-nebisys annotations that apply at an instance path of a properties schema (spec 4.2):
 * an annotation applies to the location its subschema evaluates and to everything nested inside it.
 * Conditional branches (`if`/`then`/`else`) only narrow values and carry no annotations.
 */
export function annotationsAtPath(
  rootSchema: JsonSchema,
  path: readonly (string | number)[],
): SchemaAnnotations {
  let current = evaluated(rootSchema);
  let result = current.map(ownAnnotations).reduce(merge, NO_ANNOTATIONS);
  for (const segment of path) {
    const next = current.flatMap((schema) => childSchemas(schema, segment)).flatMap(evaluated);
    if (next.length === 0) break;
    result = next.map(ownAnnotations).reduce(merge, result);
    current = next;
  }
  return result;
}

export type ReferenceKind = 'param' | 'resource' | 'secret' | 'deployment';

/** Which `${...}` references a field may use (spec 6.3.4). */
export function allowedReferenceKinds(annotations: SchemaAnnotations): ReferenceKind[] {
  if (annotations.literal) return ['param', 'deployment'];
  const kinds: ReferenceKind[] = ['param', 'resource'];
  if (annotations.secretAllowed || annotations.secretRequired) kinds.push('secret');
  kinds.push('deployment');
  return kinds;
}

export interface CompletionResource {
  id: string;
  outputs: { name: string; secret?: boolean }[];
}

export interface CompletionContext {
  kinds: readonly ReferenceKind[];
  parameters: readonly string[];
  /** Other resources and the outputs of their types. */
  resources: readonly CompletionResource[];
  /** Declared secret names. Without declarations, `secret.` is offered for a free name. */
  secrets?: readonly string[];
}

export interface CompletionItem {
  kind: ReferenceKind;
  /** The reference text, for example `param.region` or `secret.`. */
  reference: string;
  /** True for `secret.`: the user types the name. */
  open: boolean;
  detail?: string;
}

export const DEPLOYMENT_FIELDS = ['id', 'name', 'shortId'] as const;

export function completionItems(context: CompletionContext): CompletionItem[] {
  const items: CompletionItem[] = [];
  for (const kind of context.kinds) {
    switch (kind) {
      case 'param':
        for (const name of context.parameters) items.push({ kind, reference: `param.${name}`, open: false });
        break;
      case 'resource':
        for (const resource of context.resources) {
          for (const output of resource.outputs) {
            items.push({
              kind,
              reference: `resource.${resource.id}.${output.name}`,
              open: false,
              detail: output.secret ? 'secret' : undefined,
            });
          }
        }
        break;
      case 'secret':
        if (context.secrets?.length) {
          for (const name of context.secrets)
            items.push({ kind, reference: `secret.${name}`, open: false, detail: 'secret' });
        } else {
          items.push({ kind, reference: 'secret.', open: true });
        }
        break;
      case 'deployment':
        for (const field of DEPLOYMENT_FIELDS)
          items.push({ kind, reference: `deployment.${field}`, open: false });
        break;
    }
  }
  return items;
}

export interface OpenExpression {
  /** Index of `$` in `${`. */
  start: number;
  /** Text typed after `${` up to the cursor, without leading spaces. */
  prefix: string;
}

/** Finds an unclosed `${` before the cursor (an escaped `$${` doesn't count). */
export function findOpenExpression(text: string, cursor: number): OpenExpression | null {
  const before = text.slice(0, cursor);
  const start = before.lastIndexOf('${');
  if (start < 0) return null;
  if (start > 0 && before[start - 1] === '$') return null;
  const inside = before.slice(start + 2);
  if (inside.includes('}')) return null;
  return { start, prefix: inside.trimStart() };
}

export interface CompletionResult {
  start: number;
  prefix: string;
  items: CompletionItem[];
}

export function completeAt(
  text: string,
  cursor: number,
  context: CompletionContext,
): CompletionResult | null {
  const open = findOpenExpression(text, cursor);
  if (!open) return null;
  const items = completionItems(context).filter(
    (item) => item.reference.startsWith(open.prefix) && item.reference !== open.prefix,
  );
  return { start: open.start, prefix: open.prefix, items };
}

/** Inserts a completion at the open expression; returns the new text and cursor. */
export function applyCompletion(
  text: string,
  cursor: number,
  start: number,
  item: CompletionItem,
): { text: string; cursor: number } {
  const after = text.slice(cursor);
  if (item.open) {
    const inserted = '${' + item.reference;
    const closing = after.startsWith('}') ? '' : '}';
    return { text: text.slice(0, start) + inserted + closing + after, cursor: start + inserted.length };
  }
  const closing = after.startsWith('}') ? '' : '}';
  const inserted = '${' + item.reference + closing;
  const newCursor = start + inserted.length + (closing ? 0 : 1);
  return { text: text.slice(0, start) + inserted + after, cursor: newCursor };
}

const WHOLE_SECRET = /^\$\{\s*secret\.([A-Za-z0-9][A-Za-z0-9-]{0,62})?\s*\}$/;

/** The secret name of a value that is exactly one `${secret.<name>}` reference. */
export function secretNameOf(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const match = WHOLE_SECRET.exec(value);
  return match ? (match[1] ?? '') : null;
}

export function secretReference(name: string): string {
  return '${secret.' + name + '}';
}

export function containsExpression(value: JsonValue | undefined): boolean {
  if (typeof value !== 'string') return false;
  return /(^|[^$])\$\{/.test(value);
}
